/**
 * Subscription / usage links served by the BeelabX portal.
 *
 * The endpoint is public with open CORS, so the link is resolved straight from
 * the client — no round trip through our own backend. It only maps a monday
 * account id onto the unguessable usage UUIDs, it exposes no financial data.
 *
 * Kept deliberately in step with the identical module in apr-frontend
 * (SmartForm): same shape, same error handling, only APP_CODE differs.
 */

const PORTAL_BASE_URL =
  process.env.REACT_APP_PORTAL_BASE_URL || 'https://portal.beelabxapps.com';

// This app's product code in the BeelabX catalogue (Subitem Pro Templates).
// Passing it narrows an account with several apps down to a single subscription.
const APP_CODE = 'SPT';

/**
 * Resolves the human-facing usage dashboard URL for a monday account.
 *
 * @param {string|number} mondayAccountId - monday account id, e.g. 15049408.
 * @returns {Promise<string|null>} URL to embed, or null when the account has no
 *   matching subscription.
 */
const getUsagePageUrl = async (mondayAccountId) => {
  const res = await fetch(
    `${PORTAL_BASE_URL}/api/public/usage-link/${mondayAccountId}?appCode=${APP_CODE}`
  );
  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    // 404 account_not_found / no_subscriptions: the account simply has nothing
    // to show, which is not an error worth surfacing to the caller.
    if (res.status === 404) return null;
    throw new Error(body?.error || 'usage_link_request_failed');
  }

  const subscriptions = Array.isArray(body?.subscriptions) ? body.subscriptions : [];
  const forThisApp = subscriptions.filter((sub) => sub?.appCode === APP_CODE);
  const candidates = forThisApp.length ? forThisApp : subscriptions;
  // appCode usually leaves exactly one row; if an account holds several, the
  // active one is the one worth showing.
  const subscription = candidates.find((sub) => sub?.is_active) || candidates[0];

  // page_url is the dashboard; body.data / api_url return the raw JSON payload
  // and are not embeddable.
  return subscription?.page_url || null;
};

export { getUsagePageUrl };
